import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import { SelectField } from './Fields.tsx';
import { PHONE_QUERY, useMediaQuery } from './useMediaQuery.ts';

/** On a phone a row of more than this many tabs is a "Section" list (rule 6, decision 1030). */
export const PHONE_TABS_MAX = 5;

/** Room kept beside the chosen tab when it is scrolled into view, past the row's 48 px fade (styles.css). */
const FADE_PX = 48;

/** Which ends of a row that scrolls sideways have tabs hidden past them. */
interface Cut {
  readonly start: boolean;
  readonly end: boolean;
}

function cutOf(el: HTMLElement): Cut {
  const start = el.scrollLeft > 1;
  const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
  return { start, end };
}

/**
 * A row of tabs over one panel (People, Company, a project, the Operations Hub; the menu consolidation, decisions 844
 * and 855): the tab list, then `TabPanel` (or the page's own panel with the same ids) with the chosen tab's content.
 * Only the tabs this person may see are passed.
 *
 * Rule 6 of docs/specs/ui-layout-rules.md (cloud-tabs, decision 1030): when the tabs are wider than the page the row
 * scrolls sideways, never wrapping, and fades out at the edge that is cut (`tabs--more-start`, `tabs--more-end`) so
 * the hidden tabs are seen to be there; the chosen tab is always scrolled into view. On a phone a row of more than
 * five tabs is a "Section" list instead.
 */
export function Tabs<T extends string>({
  label,
  tabs,
  current,
  onChange,
  idPrefix,
}: {
  readonly label: string;
  readonly tabs: ReadonlyArray<readonly [T, string]>;
  readonly current: T;
  readonly onChange: (tab: T) => void;
  /** Keeps the ids apart from another tab row on the page: `<idPrefix>-tab-<id>` and `<idPrefix>-panel-<id>`. */
  readonly idPrefix: string;
}): ReactElement {
  const phone = useMediaQuery(PHONE_QUERY);
  if (phone && tabs.length > PHONE_TABS_MAX) {
    const name = tabs.find(([id]) => id === current)?.[1] ?? '';
    return (
      <div className="tabs-section" role="group" aria-label={label}>
        <SelectField label="Section" value={current} onChange={onChange} options={tabs} />
        {/* the panel is named after the chosen section, as it is after its tab */}
        <span id={`${idPrefix}-tab-${current}`} hidden>
          {name}
        </span>
      </div>
    );
  }
  return <TabRow label={label} tabs={tabs} current={current} onChange={onChange} idPrefix={idPrefix} />;
}

function TabRow<T extends string>({
  label,
  tabs,
  current,
  onChange,
  idPrefix,
}: {
  readonly label: string;
  readonly tabs: ReadonlyArray<readonly [T, string]>;
  readonly current: T;
  readonly onChange: (tab: T) => void;
  readonly idPrefix: string;
}): ReactElement {
  const row = useRef<HTMLDivElement>(null);
  const [cut, setCut] = useState<Cut>({ start: false, end: false });
  const measure = useCallback(() => {
    const el = row.current;
    if (!el) return;
    const next = cutOf(el);
    setCut((was) => (was.start === next.start && was.end === next.end ? was : next));
  }, []);

  // the cut edges follow the row's scroll and the window's width
  useEffect(() => {
    const el = row.current;
    if (!el) return undefined;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const seen = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    seen?.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      seen?.disconnect();
    };
  }, [measure]);

  // the chosen tab in view, clear of the fade (only the row scrolls, never the page)
  useLayoutEffect(() => {
    const el = row.current;
    const tab = el?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!el || !tab || el.scrollWidth <= el.clientWidth) return;
    const left = tab.offsetLeft;
    const right = left + tab.offsetWidth;
    if (left - FADE_PX < el.scrollLeft) el.scrollLeft = Math.max(0, left - FADE_PX);
    else if (right + FADE_PX > el.scrollLeft + el.clientWidth)
      el.scrollLeft = right + FADE_PX - el.clientWidth;
    measure();
  }, [current, tabs.length, measure]);

  return (
    <div
      ref={row}
      className={['tabs', cut.start ? 'tabs--more-start' : '', cut.end ? 'tabs--more-end' : '']
        .filter(Boolean)
        .join(' ')}
      role="tablist"
      aria-label={label}
    >
      {tabs.map(([id, text]) => (
        <button
          key={id}
          type="button"
          role="tab"
          id={`${idPrefix}-tab-${id}`}
          aria-selected={current === id}
          aria-controls={`${idPrefix}-panel-${id}`}
          className={current === id ? 'tabs__tab tabs__tab--on' : 'tabs__tab'}
          onClick={() => onChange(id)}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({
  idPrefix,
  current,
  children,
}: {
  readonly idPrefix: string;
  readonly current: string;
  readonly children: ReactElement | null;
}): ReactElement {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${current}`}
      aria-labelledby={`${idPrefix}-tab-${current}`}
      className="stack"
    >
      {children}
    </div>
  );
}
